/**
 * Past where the map was drawn. Two sealed places in the rows under the drawn
 * country (DRAWN_H and down), opened by the story in act three (docs/STORY.md,
 * spoilers):
 *
 *  - The Margin: unfinished ground, reached from the west edge of Radville.
 *  - The Terminus: where the line goes. Reached only by the coach from the
 *    Margin's platform. It loops: walk off one end and you come in the other.
 *
 * Stamped after the seed has grown the country, so nothing grown lands here.
 */
import type { EnemyKind } from './types.ts';
import { DRAWN_H, MAP_H, MAP_W, T_BLANK, T_DIRT, T_FLOOR, T_GRASS, T_SKETCH, T_TRACK, T_WALL, TILE, hash2, noise } from './tiles.ts';
import { GEN } from './worldgen.ts';

const px = (t: number) => t * TILE + 8;

/** The Margin's floor, inside its pencil border. */
export const MARGIN = { x0: 4, y0: 304, x1: 134, y1: 356 };
/** The Terminus's floor. The east and west ends are one place. */
export const TERMINUS = { x0: 150, y0: 304, x1: 420, y1: 356 };
/** Where the line runs through both. */
const LINE_Y = 330;

/** Stepping off the west edge of Radville, and back. */
export const EDGE = {
  out: { x: px(0), y: px(29) },
  outArrive: { x: px(128), y: px(LINE_Y - 1) },
  back: { x: px(132), y: px(LINE_Y) },
  backArrive: { x: px(3), y: px(29) },
};

/** The last platform, and where the coach puts you down at either end. */
export const LAST_STOP = {
  platform: { x: px(14), y: px(327) },
  coach: { x: px(16), y: px(LINE_Y + 1) },
  marginArrive: { x: px(18), y: px(327) },
  terminusCoach: { x: px(158), y: px(LINE_Y + 1) },
  terminusArrive: { x: px(163), y: px(LINE_Y - 2) },
};

/** Past Halt, the first few glow tiles of the Leak. They go quiet for anyone who stopped the count. */
export const LEAK_EDGE = { x0: 274, y0: 16, x1: 313, y1: 52 };

export function inMargin(x: number, y: number): boolean {
  const tx = Math.floor(x / TILE);
  const ty = Math.floor(y / TILE);
  return ty >= DRAWN_H && tx < 140;
}

export function inTerminus(x: number, y: number): boolean {
  const tx = Math.floor(x / TILE);
  const ty = Math.floor(y / TILE);
  return ty >= DRAWN_H && tx >= 140;
}

/** Past where the map was drawn. */
export function pastEdge(y: number): boolean {
  return Math.floor(y / TILE) >= DRAWN_H;
}

/** In the Terminus, off one end is on at the other. Returns the new x, or null if you are not at an end. */
export function terminusWrap(x: number, y: number): number | null {
  if (!inTerminus(x, y)) return null;
  const tx = Math.floor(x / TILE);
  if (tx >= TERMINUS.x1 - 1) return px(TERMINUS.x0 + 1);
  if (tx <= TERMINUS.x0) return px(TERMINUS.x1 - 2);
  return null;
}

export function stampPastEdge(m: number[][]): void {
  const fill = (x: number, y: number, w: number, h: number, t: number) => {
    for (let ty = y; ty < y + h; ty++) for (let tx = x; tx < x + w; tx++) if (ty >= DRAWN_H && ty < MAP_H && tx >= 0 && tx < MAP_W) m[ty][tx] = t;
  };
  fill(0, DRAWN_H, MAP_W, MAP_H - DRAWN_H, T_WALL);
  stampMargin(m, fill);
  stampTerminus(m, fill);
}

type Fill = (x: number, y: number, w: number, h: number, t: number) => void;

// ---------------------------------------------------------------------------
// The Margin. Painted at the east end, where you come in, and less and less
// painted going west, until it is paper. The last platform is at the west end.

function stampMargin(m: number[][], fill: Fill): void {
  fill(0, DRAWN_H, 140, MAP_H - DRAWN_H, T_SKETCH);
  const { x0, y0, x1, y1 } = MARGIN;
  for (let ty = y0; ty < y1; ty++) {
    for (let tx = x0; tx < x1; tx++) {
      // How far the painters got: all of it at the east end, none of it at the west.
      const painted = (tx - x0 - 20) / (x1 - x0 - 30);
      const n = noise(tx, ty, 7, 91);
      let t = hash2(tx, ty, 17) < painted ? (n > 0.55 ? T_DIRT : T_GRASS) : T_BLANK;
      // Walls somebody started drawing and gave up on.
      if (noise(tx, ty, 5, 23) > 0.7 && Math.abs(ty - LINE_Y) > 3) t = T_SKETCH;
      m[ty][tx] = t;
    }
  }
  // The edges wobble, like a hand drew them.
  for (let tx = x0; tx < x1; tx++) {
    if (hash2(tx, 1, 5) < 0.5) m[y0][tx] = T_SKETCH;
    if (hash2(tx, 2, 5) < 0.5) m[y1 - 1][tx] = T_SKETCH;
  }
  // The line, running the whole width, and a clear strip either side of it.
  for (let tx = x0; tx < x1; tx++) {
    for (let ty = LINE_Y - 2; ty <= LINE_Y + 2; ty++) if (m[ty][tx] === T_SKETCH) m[ty][tx] = T_BLANK;
    m[LINE_Y][tx] = T_TRACK;
    m[LINE_Y + 1][tx] = T_TRACK;
  }
  // The last platform.
  fill(8, 324, 16, 5, T_FLOOR);
  fill(5, 322, 22, 2, T_BLANK);
  // Where you come in: a short painted apron, so it looks like the world for a moment.
  fill(122, 325, 12, 11, T_GRASS);
  fill(124, LINE_Y, 10, 2, T_TRACK);

  // A pencil-free lane from any spot down (or up) to the line, so nothing is drawn shut.
  const lane = (tx: number, ty: number) => {
    for (let y = Math.min(ty, LINE_Y); y <= Math.max(ty, LINE_Y); y++) if (m[y][tx] === T_SKETCH) m[y][tx] = T_BLANK;
  };
  GEN.props.push({ kind: 'sign', x: 15 * TILE, y: 322 * TILE, text: 'RADVILLE' });
  GEN.props.push({ kind: 'bench', x: px(11), y: px(326) });
  GEN.props.push({ kind: 'bench', x: px(20), y: px(326) });
  GEN.props.push({ kind: 'lamppost', x: px(9), y: px(328) - 4 });
  GEN.props.push({ kind: 'lamppost', x: px(23), y: px(328) - 4 });
  GEN.props.push({ kind: 'buffer', x: px(5), y: px(LINE_Y + 1) });
  GEN.props.push({ kind: 'sign', x: 116 * TILE, y: 326 * TILE, text: 'POP. VARIES' });
  // Signs nobody finished.
  for (const [sx, sy] of [
    [104, 318],
    [92, 341],
    [78, 322],
    [63, 338],
    [49, 316],
    [38, 344],
    [30, 320],
  ] as const) {
    fill(sx, sy, 1, 2, T_BLANK);
    lane(sx, sy);
    GEN.props.push({ kind: 'sign', x: sx * TILE, y: sy * TILE, text: '' });
  }

  const spawns: [EnemyKind, number, number, boolean?][] = [
    ['blot', 112, 318],
    ['blot', 106, 340],
    ['blot', 98, 327],
    ['blot', 90, 314],
    ['blot', 86, 346],
    ['blot', 76, 333],
    ['blot', 70, 318],
    ['blot', 64, 344],
    ['blot', 58, 326, true],
    ['blot', 52, 336],
    ['blot', 46, 312],
    ['blot', 42, 348],
    ['blot', 40, 328],
    ['blot', 32, 336],
  ];
  for (const [kind, tx, ty, champ] of spawns) {
    fill(tx - 1, ty - 1, 3, 3, T_BLANK);
    lane(tx, ty);
    GEN.edge.push({ kind, tx, ty, roam: 3, tier: 8, champ });
  }
  fill(33, 326, 5, 3, T_BLANK);
  GEN.edge.push({ kind: 'surveyor', tx: 35, ty: 327, roam: 2, tier: 8, boss: true });
  fill(60, 348, 3, 3, T_BLANK);
  lane(61, 349);
  GEN.caches.push({ id: 'margincrate', name: 'Unmarked crate', x: px(61), y: px(349), tier: 8, face: 'crate', boost: 2.2, back: 900 });
  fill(96, 309, 3, 3, T_BLANK);
  lane(97, 310);
  GEN.caches.push({ id: 'margintrunk', name: 'Trunk with no label', x: px(97), y: px(310), tier: 8, face: 'chest', boost: 2.6, back: 1200 });
}

// ---------------------------------------------------------------------------
// The Terminus. Platforms and tracks as far as you can walk, both ways, because
// both ways are the same way.

function stampTerminus(m: number[][], fill: Fill): void {
  const { x0, y0, x1, y1 } = TERMINUS;
  fill(x0, y0, x1 - x0, y1 - y0, T_FLOOR);
  // Tracks every eight rows, the main line in the middle.
  for (const ty of [310, 318, 326, LINE_Y, 338, 346]) fill(x0, ty, x1 - x0, 2, T_TRACK);
  // Pillars on the platforms, with a clock on every third one.
  let n = 0;
  for (let tx = x0 + 8; tx < x1 - 6; tx += 12) {
    for (const ty of [306, 314, 322, 334, 342, 350]) {
      if (hash2(tx, ty, 3) < 0.25) continue;
      fill(tx, ty, 2, 2, T_WALL);
      if (n++ % 3 === 0) GEN.props.push({ kind: 'clock', x: tx * TILE + 16, y: (ty + 2) * TILE });
    }
  }
  // Patches where the floor stops being floor.
  for (let ty = y0; ty < y1; ty++) {
    for (let tx = x0; tx < x1; tx++) {
      if (m[ty][tx] !== T_FLOOR) continue;
      if (noise(tx, ty, 9, 57) > 0.72) m[ty][tx] = T_BLANK;
    }
  }
  // Where the coach puts you down.
  fill(x0 + 2, 326, 20, 4, T_FLOOR);
  fill(x0 + 2, 332, 20, 2, T_FLOOR);
  GEN.props.push({ kind: 'coach', x: LAST_STOP.terminusCoach.x, y: LAST_STOP.terminusCoach.y });
  GEN.props.push({ kind: 'sign', x: 166 * TILE, y: 325 * TILE, text: 'TERMINUS' });
  GEN.props.push({ kind: 'bench', x: px(168), y: px(327) });

  const pool: EnemyKind[] = ['clerk', 'walker', 'moth', 'leaker', 'hound', 'blot', 'nail', 'mite'];
  let i = 0;
  for (let tx = x0 + 24; tx < x1 - 6; tx += 9) {
    for (const ty of [307, 315, 323, 336, 344, 352]) {
      const r = hash2(tx, ty, 71);
      if (r < 0.62) continue;
      const kind = pool[Math.floor(hash2(tx, ty, 72) * pool.length)];
      const sx = tx + Math.floor(hash2(tx, ty, 73) * 5);
      fill(sx, ty, 1, 1, T_FLOOR);
      GEN.edge.push({ kind, tx: sx, ty, roam: 4, tier: r > 0.9 ? 10 : 9, champ: i++ % 7 === 3 });
    }
  }
  fill(284, 326, 12, 8, T_FLOOR);
  fill(284, LINE_Y, 12, 2, T_TRACK);
  GEN.edge.push({ kind: 'lastnumber', tx: 290, ty: 329, roam: 0, tier: 10, boss: true });
  const caches: [string, string, number, number, number][] = [
    ['lostprop1', 'Lost property', 206, 320, 2.8],
    ['lostprop2', 'Lost property', 252, 348, 2.8],
    ['lostprop3', 'Lost property', 338, 312, 2.8],
    ['lostprop4', 'Lost property', 396, 340, 2.8],
    ['farebox', 'The fare box', 291, 333, 3.6],
  ];
  for (const [id, name, tx, ty, boost] of caches) {
    fill(tx, ty, 1, 1, T_FLOOR);
    GEN.caches.push({ id, name, x: px(tx), y: px(ty), tier: 10, face: id === 'farebox' ? 'chest' : 'barrel', boost, back: 1500 });
  }
}
